import React, {useEffect, useMemo, useState} from 'react';
import {Icons} from '../../../assets';
import {ContactItem, ProfileHeaderCard} from '../../../components/profile-header-card';
import {useOpportunity} from '../../../contexts';
import {JobApplicationStatus} from '../../../models/job-application';
import {ApplicantStatusControls} from './applicant-status-controls';

interface ApplicantCardProps {
  applicationId: string;
  opportunityId: string;
  name: string;
  email: string;
  phone: string;
  imageUrl?: string;
  status: JobApplicationStatus;
  onViewAura?: () => void;
  onStatusUpdated?: (applicationId: string, status: JobApplicationStatus) => void;
}

export const ApplicantCard: React.FC<ApplicantCardProps> = ({
  applicationId,
  opportunityId,
  name,
  email,
  phone,
  imageUrl,
  status,
  onViewAura,
  onStatusUpdated,
}) => {
  const {updateApplicationStatus} = useOpportunity();
  const [loadingStatus, setLoadingStatus] = useState<JobApplicationStatus | null>(null);
  const [currentStatus, setCurrentStatus] = useState<JobApplicationStatus>(status);

  const handleStatusChange = async (newStatus: JobApplicationStatus) => {
    try {
      setLoadingStatus(newStatus);
      await updateApplicationStatus(opportunityId, applicationId, newStatus);
      setCurrentStatus(newStatus);
      onStatusUpdated?.(applicationId, newStatus);
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update application status. Please try again.');
    } finally {
      setLoadingStatus(null);
    }
  };

  useEffect(() => {
    // Reset local status when the viewed application changes to avoid carrying over actions between applicants
    setCurrentStatus(status);
    setLoadingStatus(null);
  }, [status, applicationId]);

  const isViewAuraDisabled = !!loadingStatus || !onViewAura;

  const viewAuraButton = onViewAura ? (
    <button
      onClick={() => onViewAura()}
      disabled={isViewAuraDisabled}
      className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 rounded-full hover:bg-gray-200 transition disabled:opacity-50">
      <span>View Aura</span>
      <img src={Icons.ARROW_UP_RIGHT} className="w-4 h-4" alt="" />
    </button>
  ) : null;

  const contactItems = useMemo<ContactItem[]>(() => {
    const items: ContactItem[] = [];
    if (email) {
      items.push({
        href: `mailto:${email}`,
        icon: Icons.EMAIL,
        label: email,
      });
    }
    if (phone) {
      items.push({
        icon: Icons.PHONE,
        label: phone,
      });
    }
    return items;
  }, [email, phone]);

  return (
    <ProfileHeaderCard
      contactItems={contactItems}
      imageUrl={imageUrl}
      name={name}
      nameActions={viewAuraButton}
      rightActions={<ApplicantStatusControls status={currentStatus} loadingStatus={loadingStatus} onChange={handleStatusChange} />}
    />
  );
};
